import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PartyPopper, Check, Zap, Crown, TrendingUp, ShieldCheck, Loader2, ArrowRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement-context";
import BrandLogo from "@/components/BrandLogo";

/**
 * UpgradeScreen - the dedicated full-page conversion experience shown when a
 * Free user clicks "Continue to Question 2" from the FreeSummaryScreen.
 *
 * NOT a modal - a full-viewport celebration screen. Follows the exact spec:
 * celebration header, progress checklist, 3-column benefits, strong primary
 * CTA + non-trapping secondary. Fully responsive.
 */
const PROGRESS_ITEMS = [
  "Fit Analysis",
  "ATS Score",
  "Top 5 Interview Questions",
  "Practice Interview",
  "Official AI Interview",
];

const BENEFIT_COLUMNS = [
  {
    title: "Prepare Smarter",
    icon: Zap,
    items: [
      "Unlimited Practice Interviews",
      "Unlimited Official AI Interviews",
      "Company-Specific Questions",
      "Unlimited Follow-up Questions",
    ],
  },
  {
    title: "Improve Faster",
    icon: TrendingUp,
    items: [
      "Unlimited AI Coaching",
      "Interview Replay",
      "Progress Tracking",
      "Unlimited Retakes",
    ],
  },
  {
    title: "Interview With Confidence",
    icon: ShieldCheck,
    items: [
      "Official Interview Readiness Score",
      "Interview Day Brief",
      "AI Interview Cheat Sheet",
      "Interview History",
    ],
  },
];

export default function UpgradeScreen() {
  const navigate = useNavigate();
  const { config, refresh, track } = useEntitlement();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    track("upgrade.screen_viewed", { variant: "full_page" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doCheckout = async (id) => {
    setBusy(id); setErr(""); setOk("");
    track("upgrade.purchase_initiated", { plan_id: id });
    try {
      const { data } = await api.post(`/billing/checkout/${id}`, {
        return_url: window.location.origin,
      });
      if (data.kind === "checkout_url" && data.url) {
        // Redirect to Stripe Checkout. Track the initiate event only; the
        // completion event is fired on /payment/success once we confirm
        // Stripe reported the session as paid.
        window.location.assign(data.url);
        return;
      }
      if (data.kind === "beta_activated") {
        setOk("Unlocked. Redirecting…");
        track("upgrade.purchase_completed", { plan_id: id });
        await refresh();
        setTimeout(() => navigate("/app/dashboard"), 900);
      } else {
        setOk(data.message || "Request received.");
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };

  const primaryLabel = config?.upgrade_cta_primary || "Continue Preparing";
  const secondaryLabel = config?.upgrade_cta_secondary || "Maybe Later";
  const headline = config?.upgrade_headline || "Great start!";
  const subheadline = config?.upgrade_subheadline || "You've completed your free interview experience.";

  return (
    <div
      data-testid="upgrade-screen"
      className="min-h-screen bg-[#0A0A0B] text-zinc-100 relative overflow-hidden"
    >
      {/* Ambient amber glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(245,158,11,0.10),transparent_55%)]" />

      {/* Top bar */}
      <header className="relative border-b border-zinc-800/70 bg-zinc-950/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BrandLogo variant="lockup" iconSize={28} to="/app/dashboard" testid="upgrade-brand" />
          <button
            onClick={() => {
              track("upgrade.maybe_later_clicked", { source: "top_bar" });
              navigate("/app/dashboard");
            }}
            data-testid="upgrade-screen-close-top"
            className="text-xs text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            {secondaryLabel}
          </button>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-10 sm:space-y-14">
        {/* Celebration header */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/[0.08] px-3 py-1 mb-6">
            <PartyPopper className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold">
              You&apos;ve completed the free experience
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-50">
            {headline}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-400 leading-relaxed">
            {subheadline}
          </p>
        </div>

        {/* Progress checklist */}
        <div
          data-testid="upgrade-progress-list"
          className="max-w-xl mx-auto rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-5 sm:p-6"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400 mb-3 text-center">
            You&apos;ve just completed
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-100">
            {PROGRESS_ITEMS.map((it) => (
              <li key={it} className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Value promise */}
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">
            Everything you need to land your next job.
          </h2>
          <p className="mt-2 text-sm text-zinc-400">
            Unlimited practice for 30 days.
          </p>
        </div>

        {/* 3-column benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {BENEFIT_COLUMNS.map((col) => {
            const Icon = col.icon;
            return (
              <div
                key={col.title}
                className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6 hover:border-amber-500/40 transition-colors"
              >
                <div className="h-10 w-10 rounded-full border border-amber-500/30 bg-amber-500/[0.08] flex items-center justify-center mb-4">
                  <Icon className="h-4 w-4 text-amber-300" />
                </div>
                <div className="text-lg font-semibold text-zinc-50 mb-3">{col.title}</div>
                <ul className="space-y-2 text-sm text-zinc-200">
                  {col.items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Errors / success */}
        {err && (
          <div data-testid="upgrade-error" className="max-w-md mx-auto text-sm text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-4 py-2.5 text-center">
            {err}
          </div>
        )}
        {ok && (
          <div data-testid="upgrade-success" className="max-w-md mx-auto text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-4 py-2.5 text-center">
            {ok}
          </div>
        )}

        {/* Primary CTA cluster */}
        <div className="max-w-2xl mx-auto text-center space-y-4">
          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3">
            <button
              onClick={() => doCheckout("interview_ready_pass")}
              disabled={!!busy}
              data-testid="upgrade-screen-primary-cta"
              className="inline-flex items-center justify-center gap-2 text-sm sm:text-base bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-xl px-6 py-3.5 shadow-[0_0_36px_-6px_rgba(245,158,11,0.7)] disabled:opacity-60 transition-colors"
            >
              {busy === "interview_ready_pass" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
              {primaryLabel} — free during beta
            </button>
            <button
              onClick={() => doCheckout("career_pro")}
              disabled={!!busy}
              data-testid="upgrade-screen-pro-cta"
              className="inline-flex items-center justify-center gap-2 text-sm sm:text-base border border-zinc-700 hover:border-amber-500/60 hover:text-amber-100 text-zinc-100 rounded-xl px-5 py-3.5 disabled:opacity-60 transition-colors"
            >
              {busy === "career_pro" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
              Career Pro
            </button>
          </div>
          <button
            onClick={() => {
              track("upgrade.maybe_later_clicked", { source: "bottom" });
              navigate("/app/dashboard");
            }}
            data-testid="upgrade-screen-maybe-later"
            className="text-sm text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            {secondaryLabel} → keep exploring
          </button>
          <div className="text-[10px] text-zinc-600 tracking-widest uppercase pt-1">
            Beta access, cancel anytime · No card required
          </div>
        </div>
      </main>
    </div>
  );
}
